import React, { Suspense } from 'react';

import { lazy } from '@loadable/component';
import cx from 'classnames';

import NullErrorBoundary from 'components/common/NullErrorBoundary';
import Box from 'UIKit/Box';

import styles from './cardsArea.module.scss';
import { CardSkeleton } from './CardSkeleton';
import { useCardsRootContext } from './CardsRootContext';
import type { TDesktopCard, TMobileCard } from './types';

/**
 * All these are dynamic cards, they render when we received correct card types in cards api.
 */

const TradeFarmingCard = lazy(
  () => import(/* webpackChunkName: "TradeFarmingCard" */ './TradeFarming')
);

const OfferTimelineCard = lazy(
  () => import(/* webpackChunkName: "OfferTimelineCard" */ './OfferTimelineCards')
);

const TopOptionsBanner = lazy(
  () =>
    import(
      /* webpackChunkName: "TopOptionsBanner" */ './TopContractsBanner/TopOptionsBanner'
    )
);

const ProductDiscoveryCard = lazy(
  () => import(/* webpackChunkName: "ProductDiscoveryCard" */ './ProductDiscovery')
);

const MilestoneOffer = lazy(
  () => import(/* webpackChunkName: "MilestoneOfferCard" */ './milestoneOffer')
);

const PersonalizedFeeCard = lazy(
  () => import(/* webpackChunkName: "PersonalizedFeeCard" */ './personalizedFeeCard')
);

const NLBCard = lazy(() => import(/* webpackChunkName: "NLBCard" */ './NLBCard/NLBCard'));
const PersonalizedAssetCard = lazy(
  () => import(/* webpackChunkName: "PersonalizedAssetCard" */ './personalizedAssetCard')
);

const DepositBonusCard = lazy(
  () =>
    import(
      /* webpackChunkName: "DepositBonusCard" */ './DepositBonusCard/DepositBonusCard'
    )
);

const OptionsNotFuturesCard = lazy(
  () => import(/* webpackChunkName: "OptionsNotFuturesCard" */ './optionsNotFuturesCard')
);

const EnhanceAccountSecurityCard = lazy(
  () =>
    import(
      /* webpackChunkName: "OptionsNotFuturesCard" */ './EnhanceAccountSecurityCard/EnhanceAccountSecurityCard'
    )
);

const ScalperOfferCard = lazy(
  () =>
    import(
      /* webpackChunkName: "ScalperOfferCard" */ './ScalperOfferCard/ScalperOfferCard'
    )
);
/**
 * All the custom cards will be kept here.
 * key should match the type being passed from the API so it could render.
 */
const CUSTOM_CARDS_MAP = {
  tradeFarming: TradeFarmingCard,
  offerTimeline: OfferTimelineCard,
  topOptions: TopOptionsBanner,
  productDiscovery: ProductDiscoveryCard,
  milestoneOffer: MilestoneOffer,
  personalizedFeeCard: PersonalizedFeeCard,
  personalizedAssetCard: PersonalizedAssetCard,
  nlbCard: NLBCard,
  depositBonusCard: DepositBonusCard,
  optionsNotFuturesCard: OptionsNotFuturesCard,
  addPhoneNumber: EnhanceAccountSecurityCard,
  scalperOffer: ScalperOfferCard,
} as const;

const CustomCardRender = ({
  card,
  className = '',
}: {
  card: TDesktopCard | TMobileCard;
  className: string;
}) => {
  const { type } = card;
  const CustomCard = CUSTOM_CARDS_MAP[type];
  const { onRequestCardToHide } = useCardsRootContext();

  if (type === 'offerTimeline') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <CustomCard
          onRequestCardToHide={() => onRequestCardToHide('offerTimeline')}
          wrapperClass={cx(styles.card, className)}
        />
      </Suspense>
    );
  }

  if (type === 'milestoneOffer') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('milestoneOffer')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'addPhoneNumber') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('addPhoneNumber')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'scalperOffer') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('scalperOffer')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'personalizedFeeCard') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('personalizedFeeCard')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'personalizedAssetCard') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('personalizedAssetCard')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'optionsNotFuturesCard') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('optionsNotFuturesCard')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'nlbCard') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary onErrorBoundaryCatch={() => onRequestCardToHide('nlbCard')}>
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  if (type === 'depositBonusCard') {
    return (
      <Suspense fallback={<CardSkeleton />}>
        <NullErrorBoundary
          onErrorBoundaryCatch={() => onRequestCardToHide('depositBonusCard')}
        >
          <CustomCard wrapperClass={cx(styles.card, className)} card={card} />
        </NullErrorBoundary>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<CardSkeleton />}>
      <NullErrorBoundary>
        <Box as="div" className={cx(styles.card, className)}>
          <CustomCard />
        </Box>
      </NullErrorBoundary>
    </Suspense>
  );
};

export { CustomCardRender, CUSTOM_CARDS_MAP };
